"use client";

import { useState } from 'react';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Star, 
  MapPin, 
  Bed, 
  Bath, 
  Home, 
  ArrowRight, 
  Clock, 
  Heart, 
  Share2, 
  Bookmark 
} from 'lucide-react';
import Link from 'next/link';
import { motion } from 'motion/react';

// Types
export interface PropertyCardProps {
  property: {
    id: string;
    imageUrl: string;
    title: string;
    address: {
      city: string;
      state: string;
      zip?: string;
    };
    description: string;
    price: number;
    reviewCount?: number;
    averageRating?: number;
    status: 'For Rent' | 'For Sale' | 'Sponsored' | 'Featured';
    slug: string;
    beds: number;
    baths: number;
    sqft: number;
    isNew?: boolean;
    isVerified?: boolean;
    daysListed?: number;
  };
  hoverEffect?: boolean;
  animationDelay?: number;
  variant?: 'default' | 'carousel';
}

// Helper function to format price in Naira
export const formatPrice = (price: number, status: string): string => {
  let formattedPrice;
  if (price >= 1000000) {
    formattedPrice = `₦${(price / 1000000).toFixed(1)}m`;
  } else if (price >= 1000) {
    formattedPrice = `₦${(price / 1000).toFixed(0)}k`;
  } else {
    formattedPrice = `₦${price}`;
  }
  
  return status === 'For Rent' ? `${formattedPrice}/mo` : formattedPrice;
};

// Render star rating
export const renderStarRating = (rating: number) => {
  return (
    <div className="flex items-center">
      {[...Array(5)].map((_, i) => (
        <Star
          key={i}
          className={`h-3.5 w-3.5 ${
            i < Math.floor(rating) 
              ? "text-[var(--color-cta-warning)] fill-[var(--color-cta-warning)]" 
              : "text-[var(--color-neutral-300)]"
          }`}
        />
      ))}
      <span className="ml-1 text-xs font-medium text-[var(--color-neutral-600)]">{rating.toFixed(1)}</span>
    </div>
  );
};

const PropertyCard = ({ 
  property, 
  hoverEffect = true,
  animationDelay = 0,
  variant = 'default' 
}: PropertyCardProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isSaved, setIsSaved] = useState(false);

  // Status color mapping
  const statusColors = {
    'For Rent': 'blue',
    'For Sale': 'green',
    'Sponsored': 'purple',
    'Featured': 'amber'
  };
  
  // Get the right color based on status
  const statusColor = statusColors[property.status as keyof typeof statusColors] || 'blue';
  
  // Helper function to get color classes based on status
  const getColorClasses = (type: string) => {
    if (type === 'badge') {
      if (statusColor === 'blue') return 'bg-[var(--color-rent-primary)] text-white border-0 shadow-md';
      if (statusColor === 'green') return 'bg-[var(--color-sale-primary)] text-white border-0 shadow-md';
      if (statusColor === 'purple') return 'bg-[var(--color-sponsored-primary)] text-white border-0 shadow-md';
      if (statusColor === 'amber') return 'bg-[var(--color-featured-primary)] text-white border-0 shadow-md';
      return 'bg-[var(--color-rent-primary)] text-white border-0 shadow-md';
    }
    
    if (type === 'icon') {
      if (statusColor === 'blue') return 'text-[var(--color-rent-primary)]';
      if (statusColor === 'green') return 'text-[var(--color-sale-primary)]';
      if (statusColor === 'purple') return 'text-[var(--color-sponsored-primary)]';
      if (statusColor === 'amber') return 'text-[var(--color-featured-primary)]';
      return 'text-[var(--color-rent-primary)]';
    }
    
    if (type === 'price') {
      if (statusColor === 'blue') return 'text-[var(--color-rent-primary)]';
      if (statusColor === 'green') return 'text-[var(--color-sale-primary)]';
      if (statusColor === 'purple') return 'text-[var(--color-sponsored-primary)]';
      if (statusColor === 'amber') return 'text-[var(--color-featured-primary)]';
      return 'text-[var(--color-rent-primary)]';
    }
    
    if (type === 'reviewButton') {
      if (statusColor === 'blue') return 'border-[var(--color-rent-secondary)] text-[var(--color-rent-primary)] hover:text-[var(--color-rent-primary)] hover:bg-[var(--color-rent-secondary)]/20';
      if (statusColor === 'green') return 'border-[var(--color-sale-secondary)] text-[var(--color-sale-primary)] hover:text-[var(--color-sale-primary)] hover:bg-[var(--color-sale-secondary)]/20';
      if (statusColor === 'purple') return 'border-[var(--color-sponsored-secondary)] text-[var(--color-sponsored-primary)] hover:text-[var(--color-sponsored-primary)] hover:bg-[var(--color-sponsored-secondary)]/20';
      if (statusColor === 'amber') return 'border-[var(--color-featured-secondary)] text-[var(--color-featured-primary)] hover:text-[var(--color-featured-primary)] hover:bg-[var(--color-featured-secondary)]/20';
      return 'border-[var(--color-rent-secondary)] text-[var(--color-rent-primary)] hover:text-[var(--color-rent-primary)] hover:bg-[var(--color-rent-secondary)]/20';
    }
    
    if (type === 'detailsButton') {
      if (statusColor === 'blue') return 'bg-[var(--color-rent-primary)] hover:bg-[var(--color-rent-primary)]/90';
      if (statusColor === 'green') return 'bg-[var(--color-sale-primary)] hover:bg-[var(--color-sale-primary)]/90';
      if (statusColor === 'purple') return 'bg-[var(--color-sponsored-primary)] hover:bg-[var(--color-sponsored-primary)]/90';
      if (statusColor === 'amber') return 'bg-[var(--color-featured-primary)] hover:bg-[var(--color-featured-primary)]/90';
      return 'bg-[var(--color-rent-primary)] hover:bg-[var(--color-rent-primary)]/90';
    }
    
    return '';
  };

  // Generate URL for property
  const getPropertyUrl = () => {
    // URL structure: /listings/[country]/[type]/[state]/[city]/[id]/[slug]/for-rent or for-sale
    const type = property.beds > 1 ? 'apartment' : 'studio';
    const listingType = property.status === 'For Rent' ? 'for-rent' : 'for-sale';
    
    return `/listings/ng/${type}/${property.address.state.toLowerCase()}/${property.address.city.toLowerCase()}/${property.id}/${property.slug}/${listingType}`;
  };

  // Generate reviews URL
  const getReviewsUrl = () => {
    const type = property.beds > 1 ? 'apartment' : 'studio';
    
    return `/listings/ng/${type}/${property.address.state.toLowerCase()}/${property.address.city.toLowerCase()}/${property.id}/${property.slug}/reviews`;
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ 
        type: 'spring',
        stiffness: 100, 
        damping: 15,
        delay: animationDelay
      }}
      className={`h-full ${variant === 'carousel' ? 'min-w-[300px] max-w-xs shrink-0' : 'w-full'}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Card className={`
        overflow-hidden border-0 bg-white 
        ${hoverEffect ? 'hover:shadow-xl transition-all duration-500' : 'shadow-lg'} 
        h-full flex flex-col relative group
      `}>
        {/* Image Container */}
        <div className="relative h-56 w-full overflow-hidden">
          <motion.img
            src={property.imageUrl}
            alt={property.title}
            className="w-full h-full object-cover"
            initial={{ scale: 1 }}
            animate={{ scale: isHovered && hoverEffect ? 1.05 : 1 }}
            transition={{ duration: 0.4 }}
          />
          
          {/* Property Status Badge */}
          <div className="absolute top-4 left-4 z-10">
            <Badge className={`px-3 py-1 text-sm font-medium ${getColorClasses('badge')}`}>
              {property.status}
            </Badge>
          </div>
          
          {/* New Property Badge */}
          {property.isNew && (
            <div className="absolute top-4 right-4 z-10">
              <Badge className="bg-[var(--color-cta-warning)] text-[var(--color-pure-white)] border-0 shadow-md px-2 py-1 text-xs font-medium">
                NEW
              </Badge>
            </div>
          )}
          
          {/* Days listed */}
          {property.daysListed !== undefined && (
            <div className="absolute bottom-4 left-4 z-10 bg-black/60 text-white text-xs px-2 py-1 rounded-md flex items-center">
              <Clock className="w-3 h-3 mr-1" />
              {property.daysListed} {property.daysListed === 1 ? 'day' : 'days'} ago
            </div>
          )}
          
          {/* Action buttons */}
          <div className="absolute right-4 bottom-4 z-10 flex space-x-2">
            <motion.button
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ 
                opacity: isHovered || isSaved ? 1 : 0,
                scale: isHovered || isSaved ? 1 : 0.8
              }}
              transition={{ duration: 0.2 }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsSaved(!isSaved);
              }}
              className={`
                p-2 rounded-full backdrop-blur-sm
                ${isSaved 
                  ? 'bg-[var(--color-cta-accent)] text-[var(--color-pure-white)]' 
                  : 'bg-[var(--color-pure-white)]/80 text-[var(--color-neutral-700)] hover:text-[var(--color-cta-accent)]'}
              `}
              aria-label="Save property"
            >
              <Heart className={`w-4 h-4 ${isSaved ? 'fill-white' : ''}`} />
            </motion.button>
            
            <motion.button
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ 
                opacity: isHovered ? 1 : 0,
                scale: isHovered ? 1 : 0.8
              }}
              transition={{ duration: 0.2, delay: 0.05 }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                // Share functionality would go here
              }}
              className="p-2 rounded-full bg-[var(--color-pure-white)]/80 backdrop-blur-sm text-[var(--color-neutral-700)] hover:text-[var(--color-brand-primary)]"
              aria-label="Share property"
            >
              <Share2 className="w-4 h-4" />
            </motion.button>
          </div>
          
          {/* Overlay gradient */}
          <motion.div 
            className="absolute inset-0 bg-linear-to-t from-black/70 via-black/10 to-transparent opacity-0 transition-opacity"
            initial={{ opacity: 0 }}
            animate={{ opacity: isHovered && hoverEffect ? 1 : 0 }}
            transition={{ duration: 0.3 }}
          />
        </div>
        
        {/* Content */}
        <CardContent className="flex-1 flex flex-col p-5">
          {/* Location */}
          <div className="flex items-center gap-2 mb-2 text-[var(--color-neutral-700)]">
            <MapPin className={`h-4 w-4 ${getColorClasses('icon')}`} />
            <span className="text-sm font-medium truncate">
              {property.address.city}, {property.address.state}
              {property.address.zip ? ` ${property.address.zip}` : ''}
            </span>
            
            {/* Verified badge */}
            {property.isVerified && (
              <Badge variant="outline" className="ml-auto text-xs border-[var(--color-cta-success)]/30 text-[var(--color-cta-success)] gap-1 flex items-center">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Verified
              </Badge>
            )}
          </div>
          
          {/* Title */}
          <h3 className="font-bold text-lg text-[var(--color-neutral-900)] mb-2 line-clamp-1 group-hover:text-[var(--color-brand-primary)] transition-colors">
            {property.title}
          </h3>
          
          {/* Description */}
          <p className="text-[var(--color-neutral-600)] text-sm mb-4 line-clamp-2">
            {property.description}
          </p>
          
          {/* Property Features */}
          <div className="flex items-center gap-4 my-3 text-[var(--color-neutral-700)] text-sm">
            <div className="flex items-center gap-1">
              <Bed className={`h-4 w-4 ${getColorClasses('icon')}`} />
              <span>{property.beds} {property.beds === 1 ? 'Bed' : 'Beds'}</span>
            </div>
            <div className="flex items-center gap-1">
              <Bath className={`h-4 w-4 ${getColorClasses('icon')}`} />
              <span>{property.baths} {property.baths === 1 ? 'Bath' : 'Baths'}</span>
            </div>
            <div className="flex items-center gap-1">
              <Home className={`h-4 w-4 ${getColorClasses('icon')}`} />
              <span>{property.sqft} sqft</span>
            </div>
          </div>
          
          {/* Price and Rating */}
          <div className="flex items-center justify-between mt-auto">
            {property.averageRating && (
              <div className="flex items-center gap-2">
                {renderStarRating(property.averageRating)}
                {property.reviewCount && (
                  <span className="text-xs text-[var(--color-neutral-500)]">({property.reviewCount})</span>
                )}
              </div>
            )}
            <div className={`text-xl font-bold ${getColorClasses('price')} ml-auto`}>
              {formatPrice(property.price, property.status)}
            </div>
          </div>
        </CardContent>
        
        {/* Footer */}
        <CardFooter className="flex gap-3 border-t border-[var(--color-neutral-200)] px-5 py-4 bg-gradient-to-r from-[var(--color-neutral-50)] to-[var(--color-pure-white)]">
          <Button 
            asChild 
            variant="outline" 
            className={`flex-1 ${getColorClasses('reviewButton')}`}
          >
            <Link href={getReviewsUrl()}>
              <Star className="h-4 w-4 mr-1" />
              Reviews
            </Link>
          </Button>
          <Button 
            asChild 
            className={`flex-1 ${getColorClasses('detailsButton')} text-white border-0`}
          >
            <Link href={getPropertyUrl()} className="flex items-center justify-center gap-1">
              Details
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
        </CardFooter>
      </Card>
    </motion.div>
  );
};

export default PropertyCard; 